import React, { useEffect, useRef, useState, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Box, Button } from "@mui/material"
import { Formik } from "formik"
import { cloneDeep } from "lodash"
import ProductionStepsResumeBanner from "./ProductionStepsResumeBanner"
import ProductionStepsTableHead from "./ProductionStepsTableHead"
import Sections from "./sections/Sections"
import ProductionStepsTable from "./ProductionStepsTable"
import { computeDisplayData, computeStepNumbers, recipeSectionsFormInitialValues } from "../../../actions/Utils/utils"
import { RecipeProductionStepsSchema } from "../../../utils/yupValidators"
import ProductionStepsContainer from "./ProductionStepsContainer"
import { getMachineTypesSelector } from "../../../reducers/Machines/machines"
import { loadMachineTypes } from "../../../actions/Machines/Machines"
import { getKitchenAreasListSelector } from "../../../reducers/Site/sites"
import { loadKitchenAreaBySiteId } from "../../../actions/Site/sites"
import { loadSupplierItemsList } from "../../../actions/Supplier/supplierItems"
import {
  loadTransformationModes
} from "../../../actions/TransformationModes/TransformationModes"
import { getTransformationModesSelector } from "../../../reducers/TransformationModes/transformationModes"
import { loadReusableProductionSteps } from "../../../actions/Steps/reusableProductionSteps."
import SwitchProductionSteps from "./SwitchProductionSteps"
import { getDefaultSteps, RECIPE_STEPS_TABLE_HEADS } from "../../../utils/recipes"
import { v4 as uuidv4 } from "uuid"
import { replacePropertyValuesFromMap } from "../../../utils"
import { formatValuesWithWeight } from "../../../utils/ressources"
import { getReusableProductionStepsListSelector } from "../../../reducers/Steps/steps"
import { getSupplierItemsListSelector } from "../../../reducers/Suppliers/SupplierItems/supplierItems"
import { updateRecipeOnChangeSectionOutput } from "../../../utils/sectionUtils"

const ProductionSteps = ({
  toggleEditForm,
  recipe,
  onCancel,
  onSave,
  isEdition = false
}) => {
  const formRef = useRef()
  const [initialValues, setInitialValues] = useState(null)
  const [switchProductionSteps, setSwitchProductionSteps] = useState(false)

  /*
  * this will not be changed,
  * unlike the initial values that changed every input of the form change
  */
  const [defaultValues, setDefaultValues] = useState(null)
  const [hoveredRow, setHoveredRow] = useState(null)
  const [fieldFocused, setFieldFocused] = useState(false)
  const [deleteHover, setDeleteHover] = useState(null)
  const [stepNumbers, setStepNumbers] = useState({})

  const machineTypes = useSelector(getMachineTypesSelector)
  const kitchenAreas = useSelector(getKitchenAreasListSelector)
  const supplierItems = useSelector(getSupplierItemsListSelector) //@TODO: this list is never used because  autocomplete reload list on focus/search
  const transformationModes = useSelector(getTransformationModesSelector)

  const allReusableSteps = useSelector(getReusableProductionStepsListSelector)

  const dispatch = useDispatch()

  // load form initial values
  useEffect(() => {
    if (!recipe) return
    const formInitialValues = recipeSectionsFormInitialValues(recipe, true)
    const stepNumbersStorage = computeStepNumbers(formInitialValues)

    setStepNumbers(stepNumbersStorage)
    setInitialValues(formInitialValues)
    setDefaultValues(cloneDeep(formInitialValues))
  }, [recipe, machineTypes])

  // load production steps select data
  useEffect(() => {
    if (machineTypes.length) return
    dispatch(loadMachineTypes(null, []))

    if (kitchenAreas.length) return
    dispatch(loadKitchenAreaBySiteId())

    if (supplierItems.length) return
    dispatch(loadSupplierItemsList())

    if (transformationModes.length) return
    dispatch(loadTransformationModes(true))

    // TODO: use autocomplete search
    if (allReusableSteps.length) return
    dispatch(loadReusableProductionSteps("list"))

  }, [machineTypes, kitchenAreas])


  useEffect(() => {
    if (!recipe) return
    const defaultChecked = !!recipe.get("switchProductionSteps")
    setSwitchProductionSteps(defaultChecked)
  }, [recipe])

  const _onRowBlur = () => {
    if (fieldFocused) return
    setHoveredRow(null)
  }


  /**
   *
   * @description this function is used to detach a step from a section and add it to the recipe steps
   * @param {object} step - the step to detach
   * @returns {void}
   */
  const _onDetachStep = (step) => {
    const indexMap = new Map()
    const currentValues = cloneDeep(formRef.current.values)
    const newValues = {
      ...currentValues,
      sections: currentValues?.sections.map((section) => {
        const stepIndex = (section?.productionSteps ?? []).findIndex((pS) => pS?.step?.index === step.index)
        if (stepIndex === -1) return section
        return {
          ...section,
          productionSteps: section?.productionSteps.reduce((acc, s, index) => {
            if (index === stepIndex) {
              delete s.reusable
              // console.log("detach step", s)
              return [...acc, ...s.step.productionSteps.map((pS) => {
                const oldStepIndex = pS.index
                delete pS.index
                delete pS.objectId
                const newStep = getDefaultSteps(pS)
                indexMap.set(oldStepIndex, newStep.index)
                const stepCompoments = (newStep.stepComponents || []).map((sc) => {
                  const scIndex = uuidv4()
                  indexMap.set(sc.index, scIndex)
                  return {
                    ...sc,
                    index: scIndex,
                  }
                })
                const { cost, realCost, reusableStepTotalGrossWeight, netWeight, grossWeight } = formatValuesWithWeight({
                  productionSteps: [{
                    stepComponents: stepCompoments
                  }],
                })
                return {
                  step: {
                    ...newStep,
                    netWeight,
                    grossWeight,
                    cost,
                    realCost,
                    stepComponents: stepCompoments,
                  },
                  cost,
                  realCost,
                  grossWeight: reusableStepTotalGrossWeight,
                  netWeight,
                  reusable: false
                }
              })]
            }
            return [...acc, s]
          }, []),
        }
      })
    }
    const reIndexedValues = replacePropertyValuesFromMap(newValues, indexMap)
    setInitialValues(reIndexedValues)
  }

  /**
   * ComponentIndex is used for productionSteps > stepComponents
   * PriorComponentIndex is used for productionSteps > stepComponents > priorSteps
   */
  const _onRowHover = (component, index, parentIndex = null, componentIndex = null, priorComponentIndex = null, stepComponentIndexesList = []) => {
    if (fieldFocused) return
    setHoveredRow({ component, index, parentIndex, componentIndex, priorComponentIndex, stepComponentIndexesList })
  }

  const _onClearFocus = () => setFieldFocused(false)

  const _onFieldFocus = () => setFieldFocused(true)

  const _onFieldBlur = (event, setFieldTouched) => {
    setFieldFocused(false)
    setFieldTouched(event.target.name)
  }

  const _onKeyUp = (event, setFieldTouched) => {
    if (!setFieldTouched) return
    setFieldTouched(event.target.name)
  }

  const _onDeleteHover = (component, index, parentIndex = null) => {
    setDeleteHover({ component, index, parentIndex })
  }

  const _onDeleteBlur = () => setDeleteHover(null)

  const handleSubmit = () => {
    if (!formRef.current) return
    formRef.current.handleSubmit()
  }

  const handleCancel = () => {
    setInitialValues(defaultValues)
    onCancel?.()
  }

  const handleSwitchProductionSteps = (checked) => setSwitchProductionSteps(checked)

  const _onSubmit = (values) => {
    const newValues = { ...values, switchProductionSteps }
    onSave(newValues)
  }

  return (
    <ProductionStepsContainer
      title={recipe?.commercialName}
      onSave={handleSubmit}
      onCancel={handleCancel}
      withTitle={isEdition}
    >
      <div>
        {/* header */}
        <Box
          className="flexRow spaceBetween center"
          sx={{ px: 4, py: isEdition ? 2 : 3 }}
        >
          <SwitchProductionSteps checked={switchProductionSteps} isEdition={isEdition} onCheck={handleSwitchProductionSteps} />
          {/* button */}
          {!isEdition && (
            <Button variant="contained" color="primary" onClick={toggleEditForm}>
              Éditer
            </Button>
          )}
        </Box>

        {/* table body */}
        <Formik
          innerRef={formRef}
          initialValues={initialValues}
          validationSchema={RecipeProductionStepsSchema}
          onSubmit={_onSubmit}
          validateOnChange={false}
          enableReinitialize
        >
          {({
            values,
            errors,
            setFieldValue,
            handleChange,
            setFieldTouched,
            setValues,
          }) => {
            const onFieldBlur = useCallback((e) => _onFieldBlur(e, setFieldTouched), [setFieldTouched])
            const onKeyUp = useCallback((e) => _onKeyUp(e, setFieldTouched), [setFieldTouched])

            const scaleRecipeToTargetNetWeight = useCallback((targetNetWeight) => {
              // return console.log("targetNetWeight", targetNetWeight)
              if (!targetNetWeight || targetNetWeight === 0) return
              const referenceNetWeight = values.netWeight
              if (!referenceNetWeight || referenceNetWeight === 0 || targetNetWeight === referenceNetWeight) return

              const ratio = targetNetWeight / referenceNetWeight
              const newFormValues = cloneDeep(values)

              // Scale leaf ingredients by ratio; recompute wrapper weights from children (never multiply stored wrapper values which may be stale)
              const scaleStepComponent = (sc) => {
                if (sc.supplierItem) {
                  sc.grossWeight = parseFloat(((sc.grossWeight || 0) * ratio).toFixed(10))
                  sc.netWeight = parseFloat(((sc.netWeight || 0) * ratio).toFixed(10))
                }
                if (sc.priorSteps) {
                  sc.priorSteps.stepComponents?.forEach(scaleStepComponent)
                  // Recompute wrapper weights from scaled children instead of multiplying
                  // the stored value, which may be stale or wrong (e.g. 542.4 instead of 200)
                  const childNet = (sc.priorSteps.stepComponents || []).reduce((sum, c) => sum + (c.netWeight || 0), 0)
                  const childGross = (sc.priorSteps.stepComponents || []).reduce((sum, c) => sum + (c.grossWeight || 0), 0)
                  sc.priorSteps.netWeight = childNet
                  sc.priorSteps.grossWeight = childGross
                  sc.netWeight = childNet
                  sc.grossWeight = childGross
                }
                if (sc.sectionOutput) {
                  sc.sectionOutput.stepComponents?.forEach(scaleStepComponent)
                }
              }

              newFormValues.sections.forEach((section) => {
                section.productionSteps?.forEach((ps) => {
                  if (!ps.reusable) {
                    ps.step?.stepComponents?.forEach(scaleStepComponent)
                  } else {
                    ps.step?.productionSteps?.forEach((subStep) => {
                      subStep.stepComponents?.forEach(scaleStepComponent)
                    })
                    // Fix: update the reusable step wrapper's own grossWeight/netWeight/cost
                    // so that the header row (ReusableStepParent) displays up-to-date values
                    const { reusableStepTotalGrossWeight, cost, realCost } = formatValuesWithWeight(ps.step)
                    ps.grossWeight = reusableStepTotalGrossWeight
                    ps.netWeight = parseFloat(((ps.netWeight || 0) * ratio).toFixed(10))
                    ps.cost = cost
                    ps.realCost = realCost
                    if (ps.step) {
                      ps.step.grossWeight = reusableStepTotalGrossWeight
                      ps.step.netWeight = ps.netWeight
                      ps.step.cost = cost
                      ps.step.realCost = realCost
                    }
                  }
                })
              })

              computeDisplayData({
                recipe: newFormValues,
                stepsField: "productionSteps",
                stepIngredientField: "stepComponents",
                isProductionSteps: true,
              })

              // Force the recipe netWeight to exactly the requested value —
              // computeDisplayData may drift due to float accumulation across steps
              newFormValues.netWeight = targetNetWeight

              newFormValues.sections.forEach((section) => {
                if (section.sectionOutputs?.length) {
                  updateRecipeOnChangeSectionOutput(newFormValues, section)
                }
              })

              setValues(newFormValues)
            }, [values, setValues])

            const scaleSectionToTargetNetWeight = useCallback((sectionIndex, targetNetWeight) => {
              const section = values.sections[sectionIndex]
              const referenceNetWeight = section?.netWeight
              if (!referenceNetWeight || referenceNetWeight === 0 || targetNetWeight === referenceNetWeight) return

              const ratio = targetNetWeight / referenceNetWeight
              const newFormValues = cloneDeep(values)

              const scaleStepComponent = (sc) => {
                if (sc.supplierItem) {
                  sc.grossWeight = parseFloat(((sc.grossWeight || 0) * ratio).toFixed(10))
                  sc.netWeight = parseFloat(((sc.netWeight || 0) * ratio).toFixed(10))
                }
                if (sc.priorSteps) {
                  sc.priorSteps.stepComponents?.forEach(scaleStepComponent)
                  const childNet = (sc.priorSteps.stepComponents || []).reduce((sum, c) => sum + (c.netWeight || 0), 0)
                  const childGross = (sc.priorSteps.stepComponents || []).reduce((sum, c) => sum + (c.grossWeight || 0), 0)
                  sc.priorSteps.netWeight = childNet
                  sc.priorSteps.grossWeight = childGross
                  sc.netWeight = childNet
                  sc.grossWeight = childGross
                }
                if (sc.sectionOutput) {
                  sc.sectionOutput.stepComponents?.forEach(scaleStepComponent)
                }
              }

              const targetSection = newFormValues.sections[sectionIndex]
              targetSection.productionSteps?.forEach((ps) => {
                if (!ps.reusable) {
                  ps.step?.stepComponents?.forEach(scaleStepComponent)
                } else {
                  ps.step?.productionSteps?.forEach((subStep) => {
                    subStep.stepComponents?.forEach(scaleStepComponent)
                  })
                  // Fix: update the reusable step wrapper's own grossWeight/netWeight/cost
                  const { reusableStepTotalGrossWeight, cost, realCost } = formatValuesWithWeight(ps.step)
                  ps.grossWeight = reusableStepTotalGrossWeight
                  ps.netWeight = parseFloat(((ps.netWeight || 0) * ratio).toFixed(10))
                  ps.cost = cost
                  ps.realCost = realCost
                  if (ps.step) {
                    ps.step.grossWeight = reusableStepTotalGrossWeight
                    ps.step.netWeight = ps.netWeight
                    ps.step.cost = cost
                    ps.step.realCost = realCost
                  }
                }
              })

              computeDisplayData({
                recipe: newFormValues,
                stepsField: "productionSteps",
                stepIngredientField: "stepComponents",
                isProductionSteps: true,
              })

              // Force the section netWeight to exactly the requested value to avoid float drift
              newFormValues.sections[sectionIndex].netWeight = targetNetWeight

              newFormValues.sections.forEach((section) => {
                if (section.sectionOutputs?.length) {
                  updateRecipeOnChangeSectionOutput(newFormValues, section)
                }
              })

              setValues(newFormValues)
            }, [values, setValues])

            return (
              <>
                {/* resume banner */}
                <ProductionStepsResumeBanner foodcost={values?.cost} netWeight={values?.netWeight} isEdition={isEdition} onNetWeightChange={scaleRecipeToTargetNetWeight} />
                {/* table */}
                <ProductionStepsTable>
                  {/* table head */}
                  <ProductionStepsTableHead headers={RECIPE_STEPS_TABLE_HEADS} hasAnyHoveredRow={!!hoveredRow} />
                  <Box className="flexColumn">
                    <Sections
                      recipe={values}
                      sections={values?.sections || []}
                      isEdition={isEdition}
                      onRowBlur={_onRowBlur}
                      onRowHover={_onRowHover}
                      hoveredRow={hoveredRow}
                      onClearFocus={_onClearFocus}
                      onFieldFocus={_onFieldFocus}
                      onFieldBlur={onFieldBlur}
                      onKeyUp={onKeyUp}
                      onDeleteHover={_onDeleteHover}
                      deleteHover={deleteHover}
                      setFieldValue={setFieldValue}
                      onDeleteBlur={_onDeleteBlur}
                      errors={errors}
                      formValues={values}
                      setValues={setValues}
                      machineTypes={machineTypes}
                      kitchenAreas={kitchenAreas}
                      supplierItems={supplierItems}
                      transformationModes={transformationModes}
                      handleChange={handleChange}
                      allReusableSteps={allReusableSteps}
                      stepNumbers={stepNumbers}
                      onDetachStep={_onDetachStep}
                      onSectionNetWeightChange={scaleSectionToTargetNetWeight}
                    />
                  </Box>
                  <div style={{ height: "200px" }} />
                </ProductionStepsTable>
              </>
            )
          }}
        </Formik>
      </div>
    </ProductionStepsContainer>
  )
}

export default ProductionSteps
